import React from "react";
import PageWrapper, { PageHero } from "../components/PageWrapper";
import LeadForm from "../components/LeadForm";
import {
  School, Building2, Globe2, Landmark, Handshake, Layers3, Sparkles,
} from "lucide-react";

const TARGETS = [
  { icon: School, title: "Schools", desc: "K-12 institutions adopting digital ecosystems and exposure programmes." },
  { icon: Building2, title: "Colleges", desc: "Higher education colleges hosting events and academic delegations." },
  { icon: Globe2, title: "Universities", desc: "Universities expanding into hybrid platforms and global mobility." },
  { icon: Landmark, title: "Governments & Councils", desc: "Public bodies digitising the education function or hosting flagship events." },
  { icon: Handshake, title: "Educational Organisations", desc: "Non-profits, foundations, and councils running global programmes." },
];

const MODELS = [
  {
    title: "White-Label Solutions",
    desc: "Deploy our platforms under your institution's or partner network's brand with full customisation.",
  },
  {
    title: "Global Expansion Partnerships",
    desc: "Regional master partnerships to bring our platforms and programmes into new geographies.",
  },
  {
    title: "Institutional Collaborations",
    desc: "Co-built initiatives with universities and councils — joint research, programmes, and platforms.",
  },
];

export default function PartnersPage() {
  return (
    <PageWrapper testId="partners-page">
      <PageHero
        eyebrow="Partner With Us"
        title="Become a global partner of Destineze."
        description="We collaborate with institutions, councils, governments, and global organisations to expand the next generation of education infrastructure."
        image="https://static.prod-images.emergentagent.com/jobs/a40b2e9c-8f18-467c-802c-d2ada63af7ec/images/3b316e15136d2092b745c600aef1af8b5f67c579efde4aaeaeb1f43095fbe4c7.png"
      />

      <section className="bg-white py-24 sm:py-28">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
              Who we partner with
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-light text-navy tracking-tight">
              We engage at the institutional level.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-3 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {TARGETS.map((t, i) => (
              <div
                key={t.title}
                data-testid={`partner-target-${i}`}
                className="border border-slate-200 p-7"
              >
                <div className="h-10 w-10 bg-navy text-gold flex items-center justify-center">
                  <t.icon size={18} strokeWidth={1.5} />
                </div>
                <div className="mt-6 font-display text-lg text-navy">
                  {t.title}
                </div>
                <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {t.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F8FAFC] py-24 sm:py-28">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
            Engagement models
          </div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-light text-navy tracking-tight max-w-3xl">
            Three structured ways to partner with us.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {MODELS.map((m, i) => (
              <div
                key={m.title}
                data-testid={`partner-model-${i}`}
                className="card-lift bg-white border border-slate-200 p-8 sm:p-10"
              >
                <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
                  Model 0{i + 1}
                </div>
                <div className="mt-2 flex items-center gap-3">
                  {i === 0 ? <Layers3 className="text-navy" /> : null}
                  {i === 1 ? <Globe2 className="text-navy" /> : null}
                  {i === 2 ? <Sparkles className="text-navy" /> : null}
                  <h3 className="font-display text-2xl text-navy font-light">
                    {m.title}
                  </h3>
                </div>
                <p className="mt-4 text-slate-600 leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-navy text-white py-24 sm:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-navy opacity-30" />
        <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold">
              Apply
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight">
              Become a global partner.
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed max-w-md">
              Tell us who you are and what you're trying to build. Our
              partnerships team will respond with a structured next step.
            </p>
          </div>
          <div className="lg:col-span-7">
            <LeadForm
              inquiryType="partnership"
              variant="dark"
              testIdPrefix="partner-lead-form"
              title="Submit a partnership inquiry"
              subtitle="Share your context. We'll respond within one business day."
            />
          </div>
        </div>
      </section>
    </PageWrapper>
  );
}
